import SVG from 'svg.js';
import eventbus from 'tiny-emitter/instance';

import forEach from 'lodash/forEach';
import first from 'lodash/first';
import last from 'lodash/last';
import groupBy from 'lodash/groupBy';

import { TYPE, FLIP, DRAWINGMODE, EVENTSTATUS } from '@/constants';

import { sortSeatsByPositionInRow, weightedBasisFunction, calculateSkewGaps } from '@/core/utils';

import {
  // getAngle,
  hypotenuseDxDy,
} from '@/core/drawing/utils';

import DrawingSeats from '@/core/drawing/drawing.seats.simple';
import DrawingSimpleShapes from '@/core/drawing/drawing.shapes.simple';
import DrawingSections from '@/core/drawing/drawing.sections';
import DrawingTables from '@/core/drawing/drawing.tables';
import DrawingTexts from '@/core/drawing/drawing.texts';
import DrawingPredefinedShapes from '@/core/drawing/drawing.predefined';
import DrawingBackgroundImage from '@/core/drawing/drawing.background';
import DrawingStagepin from '@/core/drawing/drawing.stagepin';

import Dragger from '@/core/utils/draggerBox';
import { useActions, useState } from '@/store/useStore';
import { LAYOUT } from '@/constants';
import { watch } from 'vue';

export default class DrawingMode {
  copypaste = false;
  skewStep = 5;
  drawingMode;
  transformTimer = null;
  unwatch;

  constructor(app) {
    this.app = app;
    if (!this.app.props.options.modes.map((s) => s.toLowerCase()).includes('drawing')) return this;

    this.seatmap = app.seatmap;
    this.selection = this.seatmap.selection;

    this.actions = useActions('settings');
    this.state = useState('settings');

    this.unwatch = watch(
      () => this.state.drawingMode.value,
      (mode) => {
        this.onDrawingModeChange(mode);
      }
    );

    this.setup();
    return this;
  }

  setup() {
    this.dragger = new Dragger(this.app);
    eventbus.emit('seatmap:changemode');
    this.addEventListeners();
  }

  addEventListeners() {
    // global
    SVG.on(window, 'keydown', this.keyDownHandler, this);
    eventbus.on('drawing:mode:changed', this.onDrawingModeChange, this);
    // drawing
    eventbus.on('seatmap:add:block', this.seatmap.addBlock, this.seatmap);
    eventbus.on('seatmap:add:unit', this.seatmap.addUnitFromDrawing, this.seatmap);
    eventbus.on('seatmap:add:section', this.seatmap.addUnitFromDrawing, this.seatmap);
    eventbus.on('seatmap:hide:sections', this.seatmap.hideSections, this.seatmap);
    eventbus.on('seatmap:show:sections', this.seatmap.showSections, this.seatmap);
    eventbus.on('drawing:started', this.seatmap.clearSelection, this.seatmap);
    eventbus.on('seatmap:remove:section', this.seatmap.removeSection, this.seatmap);
    // transformations
    eventbus.on('zone:flip', this.flipZone, this);
    eventbus.on('selection:skew', this.skew, this);
    eventbus.on('selection:seatSpacing', this.setSeatSpacing, this);
    eventbus.on('selection:rowSpacing', this.setRowSpacing, this);
    eventbus.on('selection:curve', this.curve, this);
    eventbus.on('selection:delayedHistory', this.delayedHistory, this);
    //dragging
    eventbus.on('drag:start', this.onDragStart, this);
    eventbus.on('drag:move', this.onDragMove, this);
    eventbus.on('drag:end', this.onDragEnd, this);
    //rotating
    eventbus.on('rotate:start', this.onRotateStart, this);
    eventbus.on('rotate:move', this.onRotateMove, this);
    eventbus.on('rotate:end', this.onRotateEnd, this);
    //resize
    eventbus.on('resize:start', this.onResizeStart, this);
    eventbus.on('resize:end', this.onResizeEnd, this);
  }

  delayedHistory(message = 'Section transform') {
    clearTimeout(this.transformTimer);
    this.transformTimer = setTimeout(() => {
      eventbus.emit('history:snapshot', message);
    }, 500);
  }

  removeEventListeners() {
    SVG.off(window, 'keydown', this.keyDownHandler);
    eventbus.off('drawing:mode:changed', this.onDrawingModeChange);
    eventbus.off('seatmap:add:block', this.seatmap.addBlock);
    eventbus.off('seatmap:add:unit', this.seatmap.addUnitFromDrawing);
    eventbus.off('seatmap:add:section', this.seatmap.addUnitFromDrawing);
    eventbus.off('seatmap:hide:sections', this.seatmap.hideSections);
    eventbus.off('seatmap:show:sections', this.seatmap.showSections);
    eventbus.off('drawing:started', this.seatmap.clearSelection);
    eventbus.off('seatmap:remove:section', this.seatmap.removeSection);
    eventbus.off('zone:flip', this.flipZone);
    eventbus.off('selection:skew', this.skew);
    eventbus.off('selection:curve', this.curve);
    eventbus.off('selection:delayedHistory', this.delayedHistory);
    eventbus.off('drag:start', this.onDragStart);
    eventbus.off('drag:move', this.onDragMove);
    eventbus.off('drag:end', this.onDragEnd);
    eventbus.off('rotate:start', this.onRotateStart);
    eventbus.off('rotate:move', this.onRotateMove);
    eventbus.off('rotate:end', this.onRotateEnd);
    eventbus.off('resize:start', this.onResizeStart);
    eventbus.off('resize:end', this.onResizeEnd);
  }

  keyDownHandler(e) {
    const isCommandKeyPressed = e.ctrlKey === true || e.metaKey === true;
    const isShiftKeyPressed = e.shiftKey === true;
    switch (e.key) {
      case 'k':
        if (this.seatmap.isHotkeysDisabled(e)) break;
        e.preventDefault();
        // TODO fix curving
        if (isCommandKeyPressed) {
          this.curve(0);
        } else {
          this.curve(10);
        }
        break;
      case 'j':
        if (this.seatmap.isHotkeysDisabled(e)) break;
        e.preventDefault();
        if (isCommandKeyPressed) {
          this.skew(true);
        } else {
          this.skew(false);
        }
        break;
      case 'Escape':
        if (this.app.layout === LAYOUT.INSIDESECTION) {
          if (this.selection.items.length) {
            this.seatmap.clearSelection();
          } else {
            this.actions.setZonesLayout();
            eventbus.emit('seatmap:labels:updated');
            this.actions.setDrawingMode();
            this.actions.changeDrawingMode(DRAWINGMODE.MAIN);
            this.actions.enableSelection();
            eventbus.emit('seatmap:exitZone:after');
          }
        } else {
          if (this.app.seatmap.store.resizedUnit) {
            const section = this.seatmap.getSectionById(this.app.seatmap.store.resizedUnit);
            if (section) {
              section.restoreBackup();
              section.endResizing();
            }
          } else {
            this.seatmap.clearSelection();
          }
        }

        break;
      case 'Delete':
      case 'Backspace':
        if (this.seatmap.isHotkeysDisabled(e)) break;
        e.preventDefault();
        this.seatmap.removeSelectedUnits();
        break;
      case 'a':
        if (document.activeElement && document.activeElement !== document.body) break;
        if (isCommandKeyPressed) {
          e.preventDefault();
          this.seatmap.selectAllUnits();
        }
        break;
      case 'c':
        if (this.seatmap.isHotkeysDisabled(e)) break;
        if (isCommandKeyPressed) {
          e.preventDefault();
          this.copypaste = true;
        }
        break;
      case 'v':
        if (this.seatmap.isHotkeysDisabled(e)) break;
        if (isCommandKeyPressed && this.copypaste === true) {
          e.preventDefault();
          this.seatmap.cloneSelection();
        }
        break;
      case 'z':
        // disabled history for now
        // if (this.seatmap.isHotkeysDisabled(e)) break;
        // if (isCommandKeyPressed) {
        //   e.preventDefault();
        //   if (isShiftKeyPressed) {
        //     eventbus.emit('history:redo');
        //   } else {
        //     eventbus.emit('history:undo');
        //   }
        // }
        break;
      case 'y':
        // disabled history for now
        // if (this.seatmap.isHotkeysDisabled(e)) break;
        // if (isCommandKeyPressed) {
        //   e.preventDefault();
        //   eventbus.emit('history:redo');
        // }
        break;
      case '37': //left
      case 'ArrowLeft': //left
        if (this.seatmap.isHotkeysDisabled(e)) break;
        if (this.selection.items.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          this.onDragStart();
          this.onDragMove({ dx: -5, dy: 0 });
          this.onDragEnd({ dx: -5, dy: 0 });
        }
        break;
      case '38': //up
      case 'ArrowUp': //up
        if (this.seatmap.isHotkeysDisabled(e)) break;
        if (this.selection.items.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          this.onDragStart();
          this.onDragMove({ dx: 0, dy: -5 });
          this.onDragEnd({ dx: 0, dy: -5 });
        }
        break;
      case '39': //right
      case 'ArrowRight': //right
        if (this.seatmap.isHotkeysDisabled(e)) break;
        if (this.selection.items.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          this.onDragStart();
          this.onDragMove({ dx: 5, dy: 0 });
          this.onDragEnd({ dx: 5, dy: 0 });
        }
        break;
      case '40': //down
      case 'ArrowDown': //down
        if (this.seatmap.isHotkeysDisabled(e)) break;
        if (this.selection.items.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          this.onDragStart();
          this.onDragMove({ dx: 0, dy: 5 });
          this.onDragEnd({ dx: 0, dy: 5 });
        }
        break;
      // case enter
      case 'Enter':
        // if (this.seatmap.isHotkeysDisabled(e)) break;
        // if (document.activeElement !== document.body) break;
        // if section is selected, enter section

        if (this.app.seatmap.store.resizedUnit) {
          const section = this.seatmap.getSectionById(this.app.seatmap.store.resizedUnit);
          if (section) {
            section.endResizing();
          }
          break;
        }

        if (
          this.selection.items.length === 1 &&
          this.selection.items[0].type === TYPE.SECTION &&
          this.selection.items[0].attributes.ga !== true
        ) {
          this.app.currentSection = this.selection.items[0];
          this.actions.enableSelection();
          if (this.app.props.status === EVENTSTATUS.PUBLISHED) {
            this.actions.setBlockSeatsMode();
          } else {
            this.actions.setDrawingMode();
          }
          this.selection.items[0].addClass('section-shape');
          this.actions.setInsideZoneLayout();
          eventbus.emit('seatmap:labels:updated');
        }
        break;
      case 'Shift':
        // if there is no active element or the active element is the body, we can continue. oterwise break
        if (document.activeElement !== document.body) break;
        {
          this.actions.setShiftKey(true);
          const items = this.selection.items.slice().filter((item) => item.type === TYPE.SECTION);
          items.forEach((section) => {
            section.showInsides();
          });
          document.addEventListener(
            'keyup',
            (e) => {
              if (e.key === 'Shift') {
                this.actions.setShiftKey(false);
                const keep = new Set([this.seatmap.store.resizeUnit, this.seatmap.store.splitModeUnit]);
                items.forEach((section) => {
                  if (keep.has(section.uuid)) return;
                  section.hideInsides();
                });
              }
            },
            { once: true, capture: true }
          );
        }
        break;
      default:
        break;
    }
  }

  onResizeStart() {
    this.seatmap.hideContextMenu();
    this.selection.items.forEach((u) => u.onResize());
  }

  onResizeEnd() {
    this.seatmap.hideContextMenu();
    this.selection.items.forEach((u) => u.onResizeEnd());
  }

  onDragStart() {
    this.seatmap.hideContextMenu();
    this.selection.items.forEach((u) => u.onDragStart());
  }

  onDragMove({ dx, dy }) {
    this.selection.items.forEach((u) => u.onDragMove(dx, dy));
  }

  onDragEnd({ dx, dy }, dragged = true) {
    this.selection.items.forEach((u) => u.onDragEnd(dx, dy, dragged));

    const groupedSeats = groupBy(
      this.selection.items.filter((seat) => !!seat.row),
      (seat) => seat.row.uuid
    );

    Object.values(groupedSeats).forEach((seats) => {
      seats[0].row.renderLabel();
    });

    if (dragged) {
      eventbus.emit('selection:updated', this.selection);
      this.seatmap.showContextMenu();
      eventbus.emit('history:snapshot', 'Move selected units');
    }
  }

  onRotateStart() {
    this.seatmap.hideContextMenu();
    this.selection.items.forEach((item) => item.onRotateStart());
  }

  onRotateMove(angle, cx, cy) {
    const rm = this.getRotationMatrix(angle, cx, cy);

    if (this.selection.items[0]?.block) {
      const grouped = groupBy(this.selection.items, (item) => item.block.uuid);
      Object.values(grouped).forEach((seats) => {
        seats[0].block.onRotateMove(rm, angle);
      });
    } else {
      this.selection.items.forEach((item) => item.onRotateMove(rm, angle));
    }
  }

  onRotateEnd(angle, cx, cy) {
    const rm = this.getRotationMatrix(angle, cx, cy);
    // this.selection.items.forEach((item) => {
    //   item.onRotateEnd(rm, angle);
    // });

    if (this.selection.items[0]?.block) {
      const grouped = groupBy(this.selection.items, (item) => item.block.uuid);
      Object.values(grouped).forEach((seats) => {
        seats[0].block.onRotateEnd(rm, angle);
      });
    } else {
      this.selection.items.forEach((item) => item.onRotateEnd(rm, angle));
    }

    eventbus.emit('selection:updated', this.selection);
    this.seatmap.showContextMenu();
    eventbus.emit('history:snapshot', 'Rotate selection');
  }

  getRotationMatrix(angle, cx, cy) {
    return new SVG.Matrix().rotate(angle, cx, cy);
  }

  static getReverseRotationMatrix(angle, cx, cy) {
    return new SVG.Matrix().rotate(-angle, cx, cy);
  }

  flipZone(section, direction) {
    const { x, y, w, h } = section.rbox();

    let pos = 0;
    if (direction === FLIP.HORIZONTALLY) {
      section.attributes.horizontalFlip = section.attributes.horizontalFlip
        ? section.attributes.horizontalFlip * -1
        : -1;
      pos = parseFloat(y, 10) + parseFloat(h, 10) / 2;
    } else {
      section.attributes.verticalFlip = section.attributes.verticalFlip ? section.attributes.verticalFlip * -1 : -1;
      pos = parseFloat(x, 10) + parseFloat(w, 10) / 2;
    }
    const fm = new SVG.Matrix().flip(direction, pos);
    const zoneViewTransform = section.view.transform('matrix');
    const zoneLabelTransform = section.label.transform('matrix');
    let zoneBox = section.rbox();
    if (section.subtype === TYPE.POLYGON || section.subtype === TYPE.RECT) {
      section.view.untransform().transform(fm.multiply(zoneViewTransform));
      zoneBox = section.rbox();
      const labelFlipPos =
        direction === FLIP.HORIZONTALLY ? section.attributes.position[1] : section.attributes.position[0];
      const newLabelTr = zoneLabelTransform.multiply(new SVG.Matrix().flip(direction, labelFlipPos));
      section.label.untransform().transform(newLabelTr);
    }

    this.seatmap.blocks
      .filter((b) => b.sid === section.uuid)
      .forEach((block) => {
        block.flip(direction, zoneBox.cx, zoneBox.cy);
      });

    eventbus.emit('history:snapshot', 'Section flip');
  }

  skew(positive, times = 1) {
    const groupedRows = this.seatmap.getGroupedRows(this.selection.items);
    forEach(groupedRows, (seatsArray) => {
      const row = first(seatsArray).row;
      const initSeatsCount = row.initialSeatsCount;
      const gaps = calculateSkewGaps(initSeatsCount, this.skewStep * times);
      if (!positive) gaps.reverse();
      forEach(seatsArray, (seat) => seat.skew(gaps[seat.pos]));
      row.renderLabel();
    });
    eventbus.emit('row:renderLabel');
    eventbus.emit('selection:updated', this.selection);
    eventbus.emit('selection:delayedHistory', 'Skew selected units');
  }

  curve(value) {
    const groupedRows = this.seatmap.getGroupedRows(this.selection.items);
    forEach(groupedRows, (seatsArray) => {
      const sortedSeats = seatsArray.sort(sortSeatsByPositionInRow);

      const firstSeat = first(sortedSeats);
      const lastSeat = last(sortedSeats);

      const [lx, ly] = firstSeat.attributes.position;
      const [rx, ry] = lastSeat.attributes.position;

      const dx = rx - lx;
      const dy = ry - ly;

      const rcx = lx + dx / 2;
      const rcy = ly + dy / 2;

      const distance = hypotenuseDxDy(dx, dy);
      const rdx = rcx + value * (dy / distance);
      const rdy = rcy - value * (dx / distance);

      forEach(seatsArray, (seat) => {
        const cdx = weightedBasisFunction(lx, rdx, rx, seat.t);
        const cdy = weightedBasisFunction(ly, rdy, ry, seat.t);
        seat.label?.untransform();
        seat.attributes.position = [cdx, cdy];
        seat.curve(cdx, cdy);
      });
    });
    eventbus.emit('selection:updated', this.selection);
    eventbus.emit('selection:delayedHistory', 'Curve selected units');
  }

  setRowSpacing(spacing) {
    this.app.seatmap.lastUsedRowSpacing = spacing / this.app.seatmap.scale;
    const groupedRows = this.seatmap.getGroupedRows(this.selection.items);
    Object.values(groupedRows)
      .map((seatsArray) => {
        return seatsArray.filter((seat) => seat.type === TYPE.SEAT && this.selection.items.includes(seat));
      })
      .reduce((acc, seatsArray) => {
        seatsArray.forEach((seat, i) => {
          if (!acc[i]) acc[i] = [];
          acc[i].push(seat);
        });
        return acc;
      }, [])
      .forEach((colArray) => {
        const len = colArray.length;

        const rotate = colArray[0].attributes.rotation || 0;
        const flip = colArray[0].block.attributes.flip ?? [false, false];
        const flipX = flip[1];

        const seatBBox = colArray[0].shape.bbox();

        // get boundingbox of all seats
        const bbox = colArray.reduce((acc, seat) => {
          return seat.shape.bbox().merge(acc);
        }, seatBBox);

        const cosTheta = Math.cos((rotate / 180) * Math.PI);
        const sinTheta = Math.sin((rotate / 180) * Math.PI);

        const seatHeights = seatBBox.height * len;

        const rotatedHeight = bbox.height * Math.abs(cosTheta) + bbox.width * Math.abs(sinTheta);
        const currentSpacing = (rotatedHeight - seatHeights) / (len - 1);

        colArray.forEach((item, i) => {
          const [x, y] = item.attributes.position;
          // item.attributes.position = [x, y + (i - len / 2) * (spacing - currentSpacing)];

          // taking flipping of the shape into account
          const flippedI = flipX ? len - i - 1 : i;

          const d = (flippedI - len / 2) * (spacing - currentSpacing);

          // Set new x spacing based on rotation
          const newX = x + d * -sinTheta;

          // Set new y spacing based on rotation
          const newY = y + d * cosTheta;

          item.attributes.position = [newX, newY];

          item.moveShapes(...item.attributes.position);
        });
      });

    eventbus.emit('row:renderLabel');
    eventbus.emit('selection:updated', this.selection);
    eventbus.emit('selection:delayedHistory', 'Set seat spacing');
  }

  setSeatSpacing(spacing) {
    this.app.seatmap.lastUsedSeatSpacing = spacing / this.app.seatmap.scale;
    const groupedRows = this.seatmap.getGroupedRows(this.selection.items);
    Object.values(groupedRows)
      .map((seatsArray) => {
        return seatsArray.filter((seat) => seat.type === TYPE.SEAT && this.selection.items.includes(seat));
      })
      .forEach((seatsArray) => {
        const len = seatsArray.length;

        const rotate = seatsArray[0].attributes.rotation || 0;
        const flip = seatsArray[0].block.attributes.flip ?? [false, false];
        const flipX = flip[0];

        const seatBBox = seatsArray[0].shape.bbox();

        // get boundingbox of all seats
        const bbox = seatsArray.reduce((acc, seat) => {
          return seat.shape.bbox().merge(acc);
        }, seatBBox);

        const cosTheta = Math.cos((rotate / 180) * Math.PI);
        const sinTheta = Math.sin((rotate / 180) * Math.PI);

        const seatWidths = seatBBox.width * len;
        // width of rotated bounding box:
        const rotatedWidth = bbox.width * Math.abs(cosTheta) + bbox.height * Math.abs(sinTheta);

        const currentSpacing = (rotatedWidth - seatWidths) / (len - 1);

        seatsArray.forEach((item, i) => {
          const [x, y] = item.attributes.position;
          // item.attributes.position = [x + (i - len / 2) * (spacing - currentSpacing), y];

          // taking flipping of the shape into account
          const flippedI = flipX ? len - i - 1 : i;

          const d = (flippedI - len / 2) * (spacing - currentSpacing);

          // Set new x spacing based on rotation
          const newX = x + d * cosTheta;

          // Set new y spacing based on rotation
          const newY = y + d * sinTheta;

          item.attributes.position = [newX, newY];

          item.moveShapes(...item.attributes.position);
        });
      });
    eventbus.emit('row:renderLabel');
    eventbus.emit('selection:updated', this.selection);
    // eventbus.emit('selection:delayedHistory', 'Set seat spacing');
  }

  onDrawingModeChange(mode) {
    this.destroyCurrentDrawingMode();
    switch (mode) {
      case DRAWINGMODE.ROWS:
        this.initDrawingSeatsMode();
        break;
      case DRAWINGMODE.SECTION.ROUNDED:
      case DRAWINGMODE.SECTION.RECTANGULAR:
      case DRAWINGMODE.SECTION.POLYGONAL:
      case DRAWINGMODE.SECTION.PATH:
        this.initDrawingSectionsMode(mode);
        break;
      case DRAWINGMODE.TEXT:
        this.initTextsMode();
        break;
      case DRAWINGMODE.BGIMAGE:
        this.initBackgroundMode();
        break;
      case DRAWINGMODE.SHAPES.SIMPLE.LINE:
      case DRAWINGMODE.SHAPES.SIMPLE.CIRCLE:
      case DRAWINGMODE.SHAPES.SIMPLE.RECT:
      case DRAWINGMODE.SHAPES.SIMPLE.POLYGON:
        this.initSimpleShapesMode(mode);
        break;
      case DRAWINGMODE.GA.ROUNDED:
      case DRAWINGMODE.GA.RECTANGULAR:
      case DRAWINGMODE.GA.POLYGONAL:
        this.initSimpleShapesMode(mode, true);
        break;
      case DRAWINGMODE.TABLE.ROUNDED:
        this.initTablesMode(TYPE.TABLE_ROUNDED);
        break;
      case DRAWINGMODE.TABLE.RECTANGULAR:
        this.initTablesMode(TYPE.TABLE_RECTANGULAR);
        break;
      case DRAWINGMODE.SHAPES.PREDEFINED.FOOD:
      case DRAWINGMODE.SHAPES.PREDEFINED.BAR:
      case DRAWINGMODE.SHAPES.PREDEFINED.EXIT:
      case DRAWINGMODE.SHAPES.PREDEFINED.BATHROOM:
      case DRAWINGMODE.SHAPES.PREDEFINED.STAGE:
      case DRAWINGMODE.SHAPES.PREDEFINED.ACCESSIBLE:
        this.initPredefinedShapesMode(mode);
        break;
      case DRAWINGMODE.STAGEPIN:
        this.initStagepinMode();
        break;
      default:
        break;
    }
  }

  initDrawingSeatsMode() {
    this.drawingMode = new DrawingSeats(this.app);
  }

  initDrawingSectionsMode(mode) {
    const type = last(mode.split(':'));
    this.drawingMode = new DrawingSections(this.app, type);
  }

  initSimpleShapesMode(mode, ga = false) {
    const type = last(mode.split(':'));
    this.drawingMode = new DrawingSimpleShapes(this.app, type, ga);
  }

  initTablesMode(type) {
    this.drawingMode = new DrawingTables(this.app, type);
  }

  initPredefinedShapesMode(mode) {
    const type = last(mode.split(':'));
    this.drawingMode = new DrawingPredefinedShapes(this.app, type);
  }

  initTextsMode() {
    this.drawingMode = new DrawingTexts(this.app);
  }

  initBackgroundMode() {
    this.drawingMode = new DrawingBackgroundImage(this.app);
  }

  initStagepinMode() {
    this.drawingMode = new DrawingStagepin(this.app);
  }

  destroyCurrentDrawingMode() {
    if (this.drawingMode) {
      this.drawingMode.destroy();
      this.drawingMode = null;
    }
  }

  removeDragger() {
    this.dragger.destroy();
    delete this.dragger;
  }

  destroy() {
    if (!this.app.props.options.modes.map((s) => s.toLowerCase()).includes('drawing')) return;
    this.destroyCurrentDrawingMode();
    this.removeDragger();
    this.removeEventListeners();
    this.unwatch();
  }
}
